'use client';

import * as React from 'react';
import Link from 'next/link';
import { shareListAtom } from 'app/blog/[domain]/_components/design-state';
import { IconFacebook } from 'icon/Brands/facebook';
import { IconLinkedIn } from 'icon/Brands/linkedin';
import { IconCheck } from 'icon/General/check';
import { IconCopy01 } from 'icon/General/copy-01';
import { IconBluesky } from 'icon/SocialMedia/bluesky';
import { IconMastodon } from 'icon/SocialMedia/mastodon';
import { IconPinterest } from 'icon/SocialMedia/pinterest';
import { IconReddit } from 'icon/SocialMedia/reddit';
import { IconThreads } from 'icon/SocialMedia/treads';
import { IconX } from 'icon/SocialMedia/x';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

type SharingProps = {
  url: string;
  title: string;
  coverImage: string;
};

export function Sharing({
  url: postUrl,
  title: postTitle,
  coverImage,
}: SharingProps) {
  const t = useTranslations('Blog.Post.Header.Sharing');
  const shareList = useAtomValue(shareListAtom);
  if (shareList.length === 0) {
    return <></>;
  }
  const shareOptions = shareList.map((shareOption) => {
    if (shareOption === 'copy') {
      return <CopyLink key="copy" />;
    }
    let icon: React.ReactNode;
    let shareUrl: string;
    let ariaLabel: string;
    const media = new URL(coverImage, postUrl);
    const url = encodeURIComponent(postUrl);
    const title = encodeURIComponent(postTitle);
    switch (shareOption) {
      case 'x':
        icon = <IconX className="post-sharing-icon" />;
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        ariaLabel = t('XLabel');
        break;
      case 'facebook':
        icon = <IconFacebook className="post-sharing-icon" />;
        shareUrl = `https://www.facebook.com/sharer.php?u=${url}&t=${title}`;
        ariaLabel = t('FacebookLabel');
        break;
      case 'linkedin':
        icon = <IconLinkedIn className="post-sharing-icon" />;
        // Can't use encodeURIComponent here
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${postUrl}`;
        ariaLabel = t('LinkedInLabel');
        break;
      case 'reddit':
        icon = <IconReddit className="post-sharing-icon" />;
        shareUrl = `https://www.reddit.com/submit?url=${url}&title=${title}`;
        ariaLabel = t('RedditLabel');
        break;
      case 'threads':
        icon = <IconThreads className="post-sharing-icon" />;
        shareUrl = `https://www.threads.net/intent/post?text=${url}`;
        ariaLabel = t('ThreadsLabel');
        break;
      case 'mastodon':
        icon = (
          <IconMastodon className="post-sharing-icon post-sharing-icon-large" />
        );
        shareUrl = `https://mastodonshare.com/?url=${url}&text=${title}`;
        ariaLabel = t('MastodonLabel');
        break;
      case 'pinterest':
        icon = <IconPinterest className="post-sharing-icon" />;
        shareUrl = `https://pinterest.com/pin/create/button/?url=${url}&description=${title}&media=${media.href}`;
        ariaLabel = t('PinterestLabel');
        break;
      case 'bluesky':
        icon = <IconBluesky className="post-sharing-icon" />;
        shareUrl = `https://bsky.app/intent/compose?text=${title}%20${url}`;
        ariaLabel = t('BlueskyLabel');
        break;
      default:
        return <></>;
    }
    return (
      <Link
        key={shareOption}
        aria-label={ariaLabel}
        href={shareUrl}
        target="_blank"
        className="post-sharing-link"
      >
        {icon}
      </Link>
    );
  });
  return <div className="post-sharing">{shareOptions}</div>;
}

export function CopyLink() {
  const t = useTranslations('Blog.Post.Header.Sharing');
  const [copied, setCopied] = React.useState(false);

  function handleCopyClick() {
    void navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <span className="post-copy-link-wrapper">
      <button
        type="button"
        onClick={handleCopyClick}
        className="post-copy-link"
      >
        {copied ? (
          <IconCheck className="post-sharing-icon" />
        ) : (
          <IconCopy01 className="post-sharing-icon" />
        )}
        {copied ? t('Copied') : t('CopyLink')}
      </button>
    </span>
  );
}
